import React, { useEffect, useMemo, useContext } from 'react';

import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import CommonConstants from 'sg-common-constants';
import SpmConstants from 'sg-spm-constants';
import SpmVehicleModifications from 'sg-spm-vehicle-modifications';
import SgVehicleUse from 'sg-spm-vehicle-use';
import SgVehicleMileage from 'sg-spm-vehicle-mileage';
import SpmVehicleLookupCtrl from 'sg-spm-vehicle-lookup';
import _ from 'lodash';
import { useSgTrackPageName } from 'saga-modules';
import { CepConstants } from 'saga-cep-specific-components';
import metadata from './SgVehicleForm.metadata.json5';
import messages from './SgVehicleForm.messages';

function SgVehicleForm(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        onValidate: onValidateParent,
        isChangingVehicle
    } = props;

    const {
        onValidate,
        isComponentValid,
    } = useValidation(id);

    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const translator = useContext(TranslatorContext);
    const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_CEP_PHONE_NUMBER;

    useSgTrackPageName(isChangingVehicle ? CepConstants.PAGE_NAMES.VEHICLE_CHANGES_REPLACE : CepConstants.PAGE_NAMES.VEHICLE_CHANGES_EDIT);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, []);

    const commonProps = {
        modelVM,
        updateData,
        showErrors,
        onValidate,
        messageParams: {
            vehicle: SpmConstants.CAR
        }
    };

    const availableYears = useMemo(() => {
        const firstYear = _.get(modelVM.value, 'lobData.privateMotor.coverables.spmVehicle.year');
        const currentYear = new Date().getUTCFullYear();
        const result = [];
        for (let i = currentYear + 1; i > firstYear; i -= 1) {
            result.push({
                code: i,
                name: i
            });
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelVM.lobData.privateMotor.coverables.spmVehicle.year.value]);

    const availableVehicleTypes = useMemo(() => {
        const availableValues = [];
        modelVM.lobData.privateMotor.coverables.spmVehicle.vehicleType.aspects.availableValues.forEach((val) => {
            if (val.code === SpmConstants.VEHICLE_CODE_AUTO || val.code === SpmConstants.VEHICLE_TYPE_MOTORHOME) {
                availableValues.push({
                    code: val.code,
                    name: translator({ id: val.name, defaultMessage: '' })
                });
            }
        });
        return availableValues;
    }, [modelVM.lobData.privateMotor.coverables.spmVehicle.vehicleType.aspects.availableValues]);

    const isVehicleLeased = useMemo(() => {
        const ownerType = _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.vehicleOwnerType.value.code');
        return ownerType === SpmConstants.OWNER_TYPE_LEASE_COMPANY || ownerType === SpmConstants.OWNER_TYPE_LEASE_SCHEME_PURCHASE;
    }, [modelVM.lobData.privateMotor.coverables.spmVehicle.vehicleOwnerType.value]);

    const overrides = {
        endorsementVehicleLookup: {
            ...commonProps,
            isCarProp: true,
            allowLookupOnly: true,
            isReadOnly: !isChangingVehicle,
            shouldCallValidation: false
        },
        vehicleOwnerType: {
            visible: isChangingVehicle
        },
        isLeaseInNameOfPhOrSpouse: {
            visible: isChangingVehicle && isVehicleLeased
        },
        vehicleType: {
            visible: isChangingVehicle,
            availableValues: availableVehicleTypes
        },
        vehicleValue: {
            visible: isChangingVehicle
        },
        isTrackerInstalled: {
            visible: isChangingVehicle && _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.vehicleValue.value.amount')
                >= SpmConstants.TRACKER_TRESHOLD_VALUE
        },
        vehicleModifications: {
            ...commonProps,
            secondaryLabel: messages.ModificationQuestionSubLabel
        },
        yearPurchased: {
            visible: isChangingVehicle,
            availableValues: availableYears
        },
        vehicleUseComponent: {
            ...commonProps,
        },
        vehicleMileageComponent: {
            ...commonProps
        },
        vehicleParkingType: {
            secondaryLabel: translator(messages.ParkingTypeSubLabel, {
                phoneNumber: isMobile
                    ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                    : contactUsPhoneNumber
            })
        }
    };
    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {
            SpmVehicleLookup: SpmVehicleLookupCtrl,
            SgVehicleUse: SgVehicleUse,
            SpmVehicleModifications: SpmVehicleModifications,
            SgVehicleMileage: SgVehicleMileage
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={modelVM}
            onModelChange={updateData}
            overrideProps={overrides}
            showErrors={showErrors}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgVehicleForm;
